/**
 * Display-time year handling for report block/section titles.
 *
 * Templates and saved reports store titles with a hard-coded data year
 * ("2023 Total Crime Index (AGS)", "2024-2029 Population: ..."), which goes
 * stale as the data moves on. At display time we rebuild the year from the data
 * vintage the report actually carries (report.dataVintages[sectionKey]); when
 * that is unknown we drop the year rather than show a possibly wrong one.
 *
 * Years that mean something fixed are kept: historical anchors
 * ("Since 2011", "(1990 - present)", "Born 1999 to 2016", census years 2000 /
 * 2010 / 2020, years inside "(Esri 2024)" titles) and ACS titles, whose
 * survey year is part of the dataset's identity.
 *
 * `title` is never rewritten in data (components also use it as a lookup key);
 * callers read `displayTitle`.
 */

const YEAR = '(?:19|20)\\d{2}';
const CENSUS_ANCHORS = new Set(['2000', '2010', '2020']);
const FORECAST_HORIZON_YEARS = 5;
// A standalone year this far ahead can only be a forecast year for a past vintage.
const FORECAST_YEAR_RANGE = [2027, 2036];

const PROTECTED_PATTERNS = [
  new RegExp(`\\bsince\\s+${YEAR}\\b`, 'gi'),
  new RegExp(`\\(\\s*${YEAR}\\s*[-–]\\s*present\\s*\\)`, 'gi'),
  new RegExp(`\\bborn\\s+${YEAR}(?:\\s*(?:to|[-–])\\s*${YEAR})?`, 'gi'),
];
const ESRI_YEAR_PAREN = new RegExp(`\\(\\s*(esri)\\s+(${YEAR})\\s*\\)`, 'i');
const RANGE = new RegExp(`\\b(${YEAR})\\s*[-–]\\s*(${YEAR})\\b`, 'g');
const SINGLE_YEAR = new RegExp(`\\b(${YEAR})\\b`, 'g');
const ANY_YEAR = new RegExp(`\\b${YEAR}\\b`);

const normalizeVintage = vintage => {
  const text = vintage == null ? '' : String(vintage).trim();
  return /^\d{4}$/.test(text) ? Number(text) : null;
};

const tidy = text =>
  text
    .replace(/\(\s*\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([,:;)])/g, '$1')
    .replace(/^[\s:,\-–]+/, '')
    .replace(/[\s:,\-–]+$/, '')
    .trim();

/**
 * @param {string} title stored title
 * @param {string|number|null|undefined} vintage data year of the title's section
 * @returns {string} title with its data year rebuilt from `vintage`, or removed when unknown
 */
export function displayBlockTitle(title, vintage) {
  if (typeof title !== 'string' || !ANY_YEAR.test(title)) return title;
  if (/\bACS\b/i.test(title)) return title;

  const v = normalizeVintage(vintage);
  const slots = [];
  const hold = value => {
    slots.push(value);
    return `\uE000${slots.length - 1}\uE001`;
  };

  let text = title;
  for (const pattern of PROTECTED_PATTERNS) text = text.replace(pattern, m => hold(m));

  // "(Esri 2024)": the year names the dataset edition; other years in such a
  // title are historical values from it.
  let historicalSeries = false;
  text = text.replace(ESRI_YEAR_PAREN, () => {
    historicalSeries = true;
    return hold(v ? `(Esri ${v})` : '(Esri)');
  });

  text = text.replace(RANGE, (_, start, end) => {
    if (historicalSeries) return hold(`${start}-${end}`);
    const a = Number(start);
    const b = Number(end);
    if (CENSUS_ANCHORS.has(start)) return hold(v ? `${a}-${v}` : '');
    if (b - a === FORECAST_HORIZON_YEARS) {
      return hold(v ? `${v}-${v + FORECAST_HORIZON_YEARS}` : '');
    }
    return hold('');
  });

  text = text.replace(SINGLE_YEAR, (match, year) => {
    if (historicalSeries || CENSUS_ANCHORS.has(year)) return match;
    const y = Number(year);
    if (y >= FORECAST_YEAR_RANGE[0] && y <= FORECAST_YEAR_RANGE[1]) {
      return v ? String(v + FORECAST_HORIZON_YEARS) : '';
    }
    return v ? String(v) : '';
  });

  text = text.replace(/\uE000(\d+)\uE001/g, (_, i) => slots[Number(i)]);
  const result = tidy(text);
  return result || title;
}

const withTitle = (item, vintage) =>
  item && typeof item === 'object' && typeof item.title === 'string'
    ? { ...item, displayTitle: displayBlockTitle(item.title, vintage) }
    : item;

/** Adds `displayTitle` to a report's sections, blocks and map blocks. `title` is untouched. */
export function withDisplayTitles(report) {
  if (!report || !Array.isArray(report.sections)) return report;
  const vintages = report.dataVintages || {};
  return {
    ...report,
    sections: report.sections.map(section => {
      const vintage = vintages[section?.key];
      const next = withTitle(section, vintage);
      if (!next || typeof next !== 'object') return next;
      return {
        ...next,
        ...(Array.isArray(section.dynamicBlocks)
          ? {
              dynamicBlocks: section.dynamicBlocks.map(block => {
                const withBlockTitle = withTitle(block, vintage);
                return block && typeof block.group === 'string'
                  ? { ...withBlockTitle, displayGroup: displayBlockTitle(block.group, vintage) }
                  : withBlockTitle;
              }),
            }
          : {}),
        ...(section.mapBlock ? { mapBlock: withTitle(section.mapBlock, vintage) } : {}),
      };
    }),
  };
}

/** Same for a combined-report group ({reports: [...]}). */
export function withGroupDisplayTitles(group) {
  if (!group || !Array.isArray(group.reports)) return group;
  return { ...group, reports: group.reports.map(withDisplayTitles) };
}

/** Title to draw: the vintage-aware one when present, else the stored title. */
export const titleOf = item => (item && (item.displayTitle ?? item.title)) ?? '';
